import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Lock, X, Eye, EyeOff, RotateCcw, Fingerprint, Delete } from 'lucide-react';

import { useTranslation } from 'react-i18next';

export default function VaultPinModal({ mode: initialMode = 'verify', onVerify, onClose, onSetup, loading, hasBiometric, onBiometricAuth, onBiometricEnroll, biometricAvailable }) {
  const {
    t
  } = useTranslation();

  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState('enter');
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    document.body.style.pointerEvents = 'auto';
    return () => { document.body.style.pointerEvents = ''; };
  }, []);

  const resetState = () => {
    setPin(''); setConfirmPin(''); setStep('enter'); setError(''); setPassword('');
  };

  const handleForgotPin = () => { resetState(); setMode('reset'); };
  const handleBackToVerify = () => { resetState(); setMode('verify'); };

  // Process a completed PIN (length 4) — same logic for both keypad and hardware keyboard
  const completeEnter = useCallback((val) => {
    if (mode === 'verify') {
      onVerify(val);
      return;
    }
    if (mode === 'reset' && !password.trim()) {
      setError('Enter your account password first');
      setPin('');
      return;
    }
    setTimeout(() => {
      setStep('confirm');
      setConfirmPin('');
    }, 150);
  }, [mode, password, onVerify]);

  const completeConfirm = useCallback((val) => {
    if (val !== pin) {
      setError("PINs don't match");
      setTimeout(() => setConfirmPin(''), 300);
    } else if (mode === 'reset') {
      onSetup(val, password);
    } else {
      onSetup(val);
    }
  }, [pin, mode, password, onSetup]);

  // ── On-screen numeric keypad ──
  // Works identically across web, iOS native (Despia), and Android native.
  // Solves the iOS WKWebView issue where programmatic .focus() on a hidden
  // <input> doesn't trigger the OS keyboard outside a user gesture chain.
  const handleKeypadDigit = useCallback((digit) => {
    setError('');
    if (step === 'confirm') {
      setConfirmPin((prev) => {
        if (prev.length >= 4) return prev;
        const next = prev + digit;
        if (next.length === 4) {
          // Defer so React renders the filled state before processing
          setTimeout(() => completeConfirm(next), 80);
        }
        return next;
      });
    } else {
      setPin((prev) => {
        if (prev.length >= 4) return prev;
        const next = prev + digit;
        if (next.length === 4) {
          setTimeout(() => completeEnter(next), 80);
        }
        return next;
      });
    }
  }, [step, completeEnter, completeConfirm]);

  const handleKeypadBackspace = useCallback(() => {
    setError('');
    if (step === 'confirm') {
      setConfirmPin((prev) => prev.slice(0, -1));
    } else {
      setPin((prev) => prev.slice(0, -1));
    }
  }, [step]);

  // Hardware keyboard support (desktop, external bluetooth keyboards)
  useEffect(() => {
    if (mode === 'enroll_biometric') return;
    const onKey = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleKeypadDigit(e.key);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleKeypadBackspace();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode, handleKeypadDigit, handleKeypadBackspace]);

  const handleBiometricTap = async () => {
    if (onBiometricAuth) {
      setError('');
      try {
        await onBiometricAuth();
      } catch (err) {
        setError('Biometric failed. Use PIN instead.');
      }
    }
  };

  const handleEnrollBiometric = async () => {
    if (!onBiometricEnroll) return;
    setEnrolling(true);
    try {
      await onBiometricEnroll();
    } catch {
      setError('Biometric enrollment failed');
    }
    setEnrolling(false);
  };

  const currentPin = step === 'confirm' ? confirmPin : pin;

  const titleMap = {
    verify: 'Enter your PIN',
    setup: step === 'confirm' ? 'Confirm your PIN' : 'Create a PIN',
    reset: step === 'confirm' ? 'Confirm new PIN' : 'Reset your PIN',
    enroll_biometric: 'Enable Biometric',
  };

  const subtitleMap = {
    verify: 'Unlock your private conversations',
    setup: step === 'confirm' ? 'Enter the same PIN again' : 'Choose a 4-digit PIN for your vault',
    reset: step === 'confirm' ? 'Enter the same PIN again' : 'Enter your password, then a new PIN',
    enroll_biometric: 'Use Face ID, Touch ID, or fingerprint to unlock faster',
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center px-5"
      style={{ background: 'rgba(0,0,0,0.85)', pointerEvents: 'auto' }}
      onClick={(e) => { if (e.target === e.currentTarget && onClose) onClose(); }}
      onTouchStart={e => e.stopPropagation()}
      onTouchMove={e => e.stopPropagation()}
      onTouchEnd={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      onPointerMove={e => e.stopPropagation()}
      onPointerUp={e => e.stopPropagation()}
    >
      <div
        className="w-full max-w-[320px] rounded-2xl p-6"
        style={{ background: 'rgba(15,23,42,0.98)', border: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="vault-pin-modal"
        onClick={e => e.stopPropagation()}
      >
        {onClose && (
          <div className="flex justify-end -mt-2 -mr-2 mb-1">
            <button onClick={onClose} className="text-slate-500 hover:text-slate-300 transition p-1.5 rounded-lg hover:bg-white/5" data-testid="vault-pin-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Biometric enrollment mode */}
        {mode === 'enroll_biometric' ? (
          <>
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3" style={{ background: 'rgba(99,102,241,0.15)' }}>
                <Fingerprint className="h-7 w-7 text-indigo-400" />
              </div>
              <h2 className="text-base font-semibold text-white" data-testid="vault-pin-title">{titleMap[mode]}</h2>
              <p className="text-xs text-slate-400 mt-0.5">{subtitleMap[mode]}</p>
            </div>
            <button
              onClick={handleEnrollBiometric}
              disabled={enrolling}
              className="w-full py-3 rounded-xl font-medium text-sm transition-all bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-50"
              data-testid="vault-enroll-biometric-btn"
            >
              {enrolling ? 'Setting up...' : 'Enable Biometric Unlock'}
            </button>
            <button
              onClick={onClose}
              className="w-full mt-2 py-2 text-xs text-slate-400 hover:text-slate-300 transition"
              data-testid="vault-skip-biometric-btn"
            >
              {t('generated.vaultpinmodal_maybe_later_8502')}
            </button>
            {error && <p className="text-center text-red-400 text-xs mt-3" data-testid="vault-pin-error">{error}</p>}
          </>
        ) : (
          <>
            <div className="text-center mb-6">
              <div
                className="inline-flex items-center justify-center w-10 h-10 rounded-xl mb-3"
                style={{ background: mode === 'reset' ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.15)' }}
              >
                {mode === 'reset' ? <RotateCcw className="h-5 w-5 text-amber-400" /> : <Lock className="h-5 w-5 text-indigo-400" />}
              </div>
              <h2 className="text-base font-semibold text-white" data-testid="vault-pin-title">{titleMap[mode]}</h2>
              <p className="text-xs text-slate-400 mt-0.5">{subtitleMap[mode]}</p>
            </div>

            {/* Biometric button - show in verify mode when biometric is enrolled */}
            {mode === 'verify' && hasBiometric && (
              <button
                onClick={handleBiometricTap}
                disabled={loading}
                className="w-full mb-4 py-3 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 bg-indigo-600/20 hover:bg-indigo-600/30 text-indigo-300 border border-indigo-500/20"
                data-testid="vault-biometric-unlock-btn"
              >
                <Fingerprint className="h-5 w-5" />
                {t('generated.vaultpinmodal_unlock_with_biometric_b361')}
              </button>
            )}

            {/* Separator when both biometric and PIN are available */}
            {mode === 'verify' && hasBiometric && (
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-wider text-slate-500">{t('generated.vaultpinmodal_or_use_pin_754a')}</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            )}

            {mode === 'reset' && step === 'enter' && (
              <div className="mb-5">
                <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('generated.vaultpinmodal_account_password_d687')}</label>
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setError(''); }}
                    className="w-full rounded-xl px-4 py-3 pr-10 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '16px' }}
                    placeholder={t('generated.vaultpinmodal_enter_your_password_339f')}
                    data-testid="vault-reset-password"
                    autoComplete="current-password"
                  />
                  <button type="button" onClick={() => setShowPw(p => !p)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 p-1">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* Visual PIN boxes — on-screen numpad below replaces OS keyboard
                (works identically on web, iOS native via Despia, Android native). */}
            <div>
              <div className="flex gap-3 justify-center mb-5" data-testid="vault-pin-boxes">
                {[0, 1, 2, 3].map(i => {
                  const filled = i < currentPin.length;
                  const isNext = i === currentPin.length;
                  return (
                    <div
                      key={i}
                      className={`w-12 h-14 rounded-xl border-2 flex items-center justify-center transition-all
                        ${filled ? 'border-indigo-500/70' : isNext ? 'border-indigo-500 ring-2 ring-indigo-500/20' : 'border-white/10'}
                      `}
                      style={{ background: 'rgba(255,255,255,0.04)' }}
                      data-testid={`vault-pin-box-${i}`}
                    >
                      {filled && <div className="w-3 h-3 rounded-full bg-white" />}
                    </div>
                  );
                })}
              </div>

              {/* On-screen numeric keypad */}
              <div
                className="grid grid-cols-3 gap-2 select-none"
                style={{ touchAction: 'manipulation' }}
                data-testid="vault-pin-keypad"
              >
                {['1','2','3','4','5','6','7','8','9'].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => handleKeypadDigit(d)}
                    className="rounded-xl py-3 text-xl font-medium text-white active:scale-95 active:bg-indigo-500/30 transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      WebkitTapHighlightColor: 'transparent',
                      minHeight: '48px',
                    }}
                    data-testid={`vault-pin-key-${d}`}
                  >
                    {d}
                  </button>
                ))}
                <div /> {/* empty cell for layout */}
                <button
                  type="button"
                  onClick={() => handleKeypadDigit('0')}
                  className="rounded-xl py-3 text-xl font-medium text-white active:scale-95 active:bg-indigo-500/30 transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: '48px',
                  }}
                  data-testid="vault-pin-key-0"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={handleKeypadBackspace}
                  className="rounded-xl py-3 flex items-center justify-center text-white active:scale-95 active:bg-indigo-500/30 transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    WebkitTapHighlightColor: 'transparent',
                    minHeight: '48px',
                  }}
                  aria-label={t('generated.vaultpinmodal_delete_33f1')}
                  data-testid="vault-pin-key-backspace"
                >
                  <Delete className="h-5 w-5" />
                </button>
              </div>
            </div>

            {error && (
              <p className="text-center text-red-400 text-xs mt-3" data-testid="vault-pin-error">{error}</p>
            )}

            {loading && (
              <div className="flex justify-center mt-3">
                <div className="h-4 w-4 border-2 border-indigo-400/30 border-t-indigo-400 rounded-full animate-spin" />
              </div>
            )}

            <div className="mt-5 text-center">
              {mode === 'verify' ? (
                <button
                  onClick={handleForgotPin}
                  className="text-xs text-indigo-400 hover:text-indigo-300 transition py-1 px-3"
                  data-testid="vault-forgot-pin"
                >
                  {t('generated.vaultpinmodal_forgot_pin_e10c')}
                </button>
              ) : mode === 'reset' && step === 'enter' ? (
                <button
                  onClick={handleBackToVerify}
                  className="text-xs text-slate-400 hover:text-slate-300 transition py-1 px-3"
                  data-testid="vault-back-to-verify"
                >
                  {t('generated.vaultpinmodal_back_to_unlock_8055')}
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
